import { useState, useEffect } from "react";
import { LuCalendar, LuChevronDown } from "react-icons/lu";
import { isSameDay } from "date-fns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import DateRangePicker from "@/components/common/calendar/DateRangePicker";
import type { DateRangeWithLabel } from "@/utils/dateUtils";
import { getPredefinedRanges, formatForAPI, getDefaultSevenDay } from "@/utils/dateUtils";
import { TimeInterval } from "@/types";
import { TZDate } from "@date-fns/tz";
import { fromZonedTime } from "date-fns-tz";
import { ReportDate } from "@/types";

type Props = {
  selectedDate: ReportDate | null;
  onChangeApply: (date: ReportDate | null) => void;
  disabled?: boolean;
  first_allowed_date?: TZDate;
  last_allowed_date?: TZDate;
  timezone?: string;
};

const ReportDateSelector = ({
  selectedDate,
  onChangeApply,
  disabled,
  first_allowed_date,
  last_allowed_date,
  timezone,
}: Props) => {
  const [isIntervalDropdownOpen, setIsIntervalDropdownOpen] = useState<boolean>(false);
  const [tempSelectedDate, setTempSelectedDate] = useState<DateRangeWithLabel | null>(
    () => {
      const allowedDateRange =
        first_allowed_date && last_allowed_date
          ? { first_allowed_date, last_allowed_date }
          : null;

      return getDefaultSevenDay(allowedDateRange, timezone ?? "UTC");
    },
  );

  // Sync tempSelectedDate when dropdown closes
  useEffect(() => {
    if (!isIntervalDropdownOpen && selectedDate) {
      try {
        const startDate = new TZDate(
          fromZonedTime(selectedDate.start_date, timezone ?? "UTC"),
          timezone ?? "UTC",
        );
        const endDate = new TZDate(
          fromZonedTime(selectedDate.end_date, timezone ?? "UTC"),
          timezone ?? "UTC",
        );

        // Set to start of day and end of day in target timezone
        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(23, 59, 59, 999);
        const predefinedRanges = getPredefinedRanges(timezone ?? "UTC");
        let label = TimeInterval.CUSTOM;
        if (selectedDate?.label && selectedDate?.label !== TimeInterval.CUSTOM) {
          const preEndDate = predefinedRanges[selectedDate.label]?.to;
          if (preEndDate && isSameDay(preEndDate, endDate)) {
            label = selectedDate.label;
          }
        }
        setTempSelectedDate({
          from: startDate,
          to: endDate,
          label: label,
        });
      } catch (error) {
        console.error("Error parsing dates:", error);
        setTempSelectedDate(null);
      }
    }
  }, [isIntervalDropdownOpen, selectedDate, timezone]);

  return (
    <DropdownMenu
      open={isIntervalDropdownOpen}
      onOpenChange={(val) => setIsIntervalDropdownOpen(val)}
    >
      <DropdownMenuTrigger
        disabled={disabled}
        className="flex h-8 w-full items-center justify-between whitespace-nowrap rounded-[8px] border-0 squircle squircle-smooth-[1] squircle-outline-[1px] squircle-outline-color-border-gray bg-white hover:bg-hover-gray px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-0"
      >
        <div className="mr-2 w-full flex items-center gap-2">
          <LuCalendar size={14} className="text-brand-primary-400 flex-shrink-0" />
          <span
            className={`${
              tempSelectedDate?.label === TimeInterval.CUSTOM ? "hidden" : ""
            } flex-1 line-clamp-1 text-brand-primary-800 text-[11px] font-medium translate-y-[1px]`}
          >
            {tempSelectedDate ? tempSelectedDate.label : "Select date range"}
          </span>
          <span
            className={`shrink-0 overflow-hidden ${
              tempSelectedDate?.label === TimeInterval.CUSTOM
                ? "text-brand-primary-800 font-medium"
                : "text-brand-primary-600 font-normal"
            } text-ellipsis text-[11px] translate-y-[1px]`}
          >
            {tempSelectedDate
              ? isSameDay(tempSelectedDate.from, tempSelectedDate.to)
                ? tempSelectedDate.from.toLocaleDateString("en-CA", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: timezone ?? "UTC",
                  })
                : `${tempSelectedDate.from.toLocaleDateString("en-CA", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: timezone ?? "UTC",
                  })} - ${tempSelectedDate.to.toLocaleDateString("en-CA", {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                    timeZone: timezone ?? "UTC",
                  })}`
              : "Select Date range"}
          </span>
        </div>
        {!disabled && (
          <LuChevronDown size={14} className="text-brand-primary-400 flex-shrink-0" />
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="p-0 border-none rounded-[12px]">
        <DateRangePicker
          selected={tempSelectedDate}
          hideCancelButton={true}
          onSelect={(range) => {
            if (range) {
              // Create a deep copy of the range to prevent any unintended modifications
              const newRange = {
                from: new TZDate(range.from, timezone ?? "UTC"),
                to: new TZDate(range.to, timezone ?? "UTC"),
                label: range.label || TimeInterval.CUSTOM,
              };
              setTempSelectedDate(newRange);
            }
          }}
          dateAggregate=""
          onApply={(range) => {
            if (range) {
              onChangeApply({
                start_date: formatForAPI(range.from, timezone ?? "UTC"),
                end_date: formatForAPI(range.to, timezone ?? "UTC"),
                label: range.label,
              });
              setIsIntervalDropdownOpen(false);
            }
          }}
          onCancel={() => setIsIntervalDropdownOpen(false)}
          autoAllowed={false}
          showAggregate={false}
          first_allowed_date={first_allowed_date}
          last_allowed_date={last_allowed_date}
          timezone={timezone ?? "UTC"}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ReportDateSelector;
